/**
 * ============================================================================
 * NOTIFICATION STORE - ZUSTAND
 * ============================================================================
 * 
 * Global notification state management
 * 
 * @typedef {Object} Notification
 * @property {string} id
 * @property {boolean} read
 * @property {string} type
 * @property {string} message
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { validateId, validateArray, validateObject, validateNotification } from './utils/validators';
import { addItemOptimized, limitArraySize, deduplicateById } from './utils/performance';
import { createSelector } from './utils/selectors';

const useNotificationStore = create(
  persist(
    (set, get) => ({
      // State
      notifications: [],
      unreadCount: 0,
      preferences: null,
      statistics: null,
      loading: false,
      error: null,
      filters: {
        read: null,
        type: null,
        dateRange: null,
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
      },

      // Actions
      /**
       * Set notifications with validation and size limit
       * @param {Notification[]} notifications - Notifications array
       */
      setNotifications: (notifications) => {
        validateArray(notifications, 'Notifications');
        const validated = notifications.map((n) => {
          if (n.id) validateNotification(n);
          return n;
        });
        const deduplicated = deduplicateById(validated);
        const limited = limitArraySize(deduplicated, 1000);
        const unreadCount = limited.filter((n) => !n.read).length;
        set({ notifications: limited, unreadCount });
      },

      /**
       * Add notification with validation, deduplication, and unread count update
       * @param {Notification} notification - Notification object
       */
      addNotification: (notification) => {
        validateNotification(notification);
        if (!notification.id) {
          notification.id = `notification-${Date.now()}-${Math.random()}`;
        }
        set((state) => {
          const updated = addItemOptimized(state.notifications, notification, {
            idKey: 'id',
            maxSize: 1000,
            prepend: true,
          });
          return {
            notifications: updated,
            unreadCount: notification.read
              ? state.unreadCount
              : state.unreadCount + 1,
          };
        });
      },

      updateNotification: (notificationId, updates) => {
        set((state) => {
          const notification = state.notifications.find(
            (n) => n.id === notificationId
          );
          const wasRead = notification?.read;
          const willBeRead = updates.read !== undefined ? updates.read : wasRead;

          return {
            notifications: state.notifications.map((n) =>
              n.id === notificationId ? { ...n, ...updates } : n
            ),
            unreadCount:
              wasRead !== willBeRead
                ? willBeRead
                  ? Math.max(0, state.unreadCount - 1)
                  : state.unreadCount + 1
                : state.unreadCount,
          };
        });
      },

      /**
       * Remove notification with validation
       * @param {string} notificationId - Notification ID
       */
      removeNotification: (notificationId) => {
        validateId(notificationId, 'Notification ID');
        set((state) => {
          const notification = state.notifications.find(
            (n) => n.id === notificationId
          );
          return {
            notifications: state.notifications.filter(
              (n) => n.id !== notificationId
            ),
            unreadCount: notification?.read
              ? state.unreadCount
              : Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAsRead: (notificationId) => {
        set((state) => {
          const notification = state.notifications.find(
            (n) => n.id === notificationId
          );
          if (!notification || notification.read) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === notificationId ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAsUnread: (notificationId) => {
        set((state) => {
          const notification = state.notifications.find(
            (n) => n.id === notificationId
          );
          if (!notification || !notification.read) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === notificationId ? { ...n, read: false } : n
            ),
            unreadCount: state.unreadCount + 1,
          };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      setUnreadCount: (count) => {
        set({ unreadCount: count });
      },

      incrementUnreadCount: () => {
        set((state) => ({
          unreadCount: state.unreadCount + 1,
        }));
      },

      decrementUnreadCount: () => {
        set((state) => ({
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
      },

      setPreferences: (preferences) => {
        set({ preferences });
      },

      updatePreferences: (updates) => {
        set((state) => ({
          preferences: state.preferences
            ? { ...state.preferences, ...updates }
            : updates,
        }));
      },

      setStatistics: (statistics) => {
        set({ statistics });
      },

      setFilters: (filters) => {
        set((state) => ({
          filters: { ...state.filters, ...filters },
          pagination: { ...state.pagination, page: 1 },
        }));
      },

      clearFilters: () => {
        set({
          filters: {
            read: null,
            type: null,
            dateRange: null,
          },
          pagination: { ...get().pagination, page: 1 },
        });
      },

      setPagination: (pagination) => {
        set((state) => ({
          pagination: { ...state.pagination, ...pagination },
        }));
      },

      setLoading: (loading) => {
        set({ loading });
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set({
          notifications: [],
          unreadCount: 0,
          preferences: null,
          statistics: null,
          loading: false,
          error: null,
          filters: {
            read: null,
            type: null,
            dateRange: null,
          },
          pagination: {
            page: 1,
            pageSize: 20,
            total: 0,
          },
        });
      },
    }),
    {
      name: 'notification-storage',
      partialize: (state) => ({
        preferences: state.preferences,
        unreadCount: state.unreadCount,
        // Don't persist notifications - fetch fresh
      }),
    }
  )
);

// Memoized Selectors
export const selectUnreadNotifications = createSelector(
  (state) => {
    return state.notifications.filter((n) => !n.read);
  }
);

export const selectNotificationsByType = createSelector(
  (state, type) => {
    return state.notifications.filter((n) => n.type === type);
  }
);

export const selectNotificationCount = createSelector(
  (state) => {
    return {
      total: state.notifications.length,
      unread: state.unreadCount,
      read: state.notifications.length - state.unreadCount,
    };
  }
);

export default useNotificationStore;
